import React, { useContext, useEffect, useImperativeHandle, forwardRef, useState } from 'react';
import { UserContext } from "../../contexts/UserContext";
import { useOrganizations } from '../../contexts/OrganizationsContext'; // <-- new import
import {
  Users,
  Award,
  //Bell,
  Settings,
  CircleHelp,
  LogOut,
  Star,
  TrendingUp,
  // AlertCircle,
  // CheckCircle,
  MessageCircle
} from 'lucide-react';
import { ChatProvider } from "../../contexts/ChatContext";
import ChatComponent from '../Chat/ChatComponent';
import StudentCard from '../Seating/StudentCard';
import OrganizationJobAssignmentsPopup from '../Jobs/JobAssignmentsPopup';
import TeacherJobsManagement from '../Jobs/TeacherJobsManagement';
import CouponGenerator from '../Acorn/CouponGenerator';
import TeacherStudent from './TeacherStudent';
import ShopManagement from '../Store/ShopManagement';
import { Notifications } from '../Notifications/Notifications';
import UserManagement from '../Users/UserManagement';
import HallPasses from '../HallPasses/HallPasses';
import GuidePanel from '../common/GuidePanel';
import teacherGuide from '../../assets/teacher-readme.md?raw';
import QRScanner from '../Acorn/QRScanner';

const TeacherDashboard = forwardRef(({ onOpenProfileSettings }: { onOpenProfileSettings?: () => void }, ref: any) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [showGuide, setShowGuide] = useState(false);
  const [selectedFilter] = React.useState<'all' | 'active' | 'warning' | 'attention'>('all');
  const { handleLogout, userProfile, hasClaim } = useContext(UserContext);
  const [showChat, setShowChat] = React.useState(false);
  const [/* showOrganizationJobs */, setShowOrganizationJobs] = React.useState(false);
  const [showOrganizationJobAssignments, setShowOrganizationJobAssignments] = React.useState(false);
  const [selectedStudent, setSelectedStudent] = React.useState<any>(null);
  const [selectedOrganization, setSelectedOrganization] = React.useState<string>("");
  const [deepLinkAssignment, setDeepLinkAssignment] = React.useState<any>(null);
  const [localShowJobs, setLocalShowJobs] = React.useState(false);
  const [localShowCoupons, setLocalShowCoupons] = React.useState(false);
  const [localShowShop, setLocalShowShop] = React.useState(false);
  const [localShowUserManagement, setLocalShowUserManagement] = React.useState(false);
  const [localShowHallPasses, setLocalShowHallPasses] = useState(userProfile?.mainRole === 'TEACHER');
  const [showCouponReissue, setShowCouponReissue] = useState(false);

  // ADD: trigger a refresh timestamp every minute for security role
  const [refreshTimestamp, setRefreshTimestamp] = useState<number>(Date.now());

  useEffect(() => {
    if (userProfile?.mainRole === 'CAMPUSSECURITYOFFICER') {
      const interval = setInterval(() => {
        setRefreshTimestamp(Date.now());
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [userProfile?.mainRole]);

  const [jobTab, setJobTab] = useState<'positions' | 'students' | 'applications'>('positions');
  const [couponTab, setCouponTab] = useState<'generate' | 'history' | 'redemptions'>('generate');
  const [shopTab, setShopTab] = useState<'products' | 'auctions' | 'transactions' | 'orders'>('products');

  // Expose an openJobs() method via ref
  const clearSubmenu = () => {
    setLocalShowJobs(false);
    setLocalShowCoupons(false);
    setLocalShowShop(false);
    setLocalShowUserManagement(false);
    setLocalShowHallPasses(false);
  };

  useImperativeHandle(ref, () => ({
    openJobs: () => {
      clearSubmenu();
      setLocalShowJobs(true);
    },
    showDashboard: () => clearSubmenu(),
    showCoupons: () => {
      clearSubmenu();
      setLocalShowCoupons(true);
    },
    showShop: () => {
      clearSubmenu();
      setLocalShowShop(true);
    },
    showUserManagement: () => {
      clearSubmenu();
      setLocalShowUserManagement(true);
    },
    showHallPasses: () => {
      clearSubmenu();
      setLocalShowHallPasses(true);
    },
    showSummons: () => {
      clearSubmenu();
      setLocalShowHallPasses(true);
      // dispatch after mounting so the listener is active
      queueMicrotask(() => window.dispatchEvent(new Event('openSummons')));
    },
    showCouponReissue: () => {
      setShowCouponReissue(true);
    }
  }));

  // Get students from OrganizationsContext instead of using dummy data.
  const { students: orgStudents, organizations } = useOrganizations();

  // useEffect to set default organization if organizations exist
  useEffect(() => {
    if (organizations && organizations.length && !selectedOrganization) {
      setSelectedOrganization(organizations[0].organizationName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations]);

  // Build new mappedStudents array
  const mappedStudents = orgStudents.map((orgUser: any) => {
    const u = orgUser?.acornUser || {};
    return {
      id: u.id,
      name: u.fullName || '',
      avatar: u.avatar || 'https://via.placeholder.com/100',
      grade: `Grade ${u.grade}`,
      points: u.points,
      level: 3,
      recentAchievements: 2,
      status: 'active',
      lastActive: u.lastActive || '1 hour ago',
      school: u.school || '',
      jobAssignment: u.jobAssignment // added
    };
  });

  // Update the filter using the context’s students.
  const filteredStudents = mappedStudents.filter((student: any) => {
    const matchesSearch = (student.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = selectedFilter === 'all' || student.status === selectedFilter;
    const matchesOrganization = selectedOrganization === "<ALL>" ? true : student.school === selectedOrganization;
    return matchesSearch && matchesFilter && matchesOrganization;
  });

  const getStatusColor = (status: any) => {
    switch (status) {
      case 'active':
        return 'bg-green-50 text-green-600';
      case 'warning':
        return 'bg-yellow-50 text-yellow-600';
      case 'attention':
        return 'bg-red-50 text-red-600';
      default:
        return 'bg-gray-50 text-gray-600';
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="flex-grow transition-all duration-300">
        <div className="dashboard-bg-alt">
          {/* Header */}
          <div className="bg-white/60 backdrop-blur-md border-b border-indigo-100/50 sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                <div className="flex items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-gray-800">{userProfile?.org}</span>
                  </div>

                  {localShowCoupons && (
                    <div className="ml-8 flex space-x-4">
                      <button
                        onClick={() => setCouponTab('generate')}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${couponTab === 'generate'
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                      >
                        Generate
                      </button>
                      <button
                        onClick={() => setCouponTab('history')}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${couponTab === 'history'
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                      >
                        History
                      </button>
                      <button
                        onClick={() => setCouponTab('redemptions')}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${couponTab === 'redemptions'
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                      >
                        Redemptions
                      </button>
                    </div>
                  )}
                  {localShowJobs && (
                    <div className="ml-8 flex space-x-4">
                      <button
                        onClick={() => {
                          setJobTab('positions');
                          setLocalShowJobs(true);
                        }}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${jobTab === 'positions'
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                      >
                        Positions
                      </button>
                      <button
                        onClick={() => {
                          setJobTab('students');
                          setLocalShowJobs(true);
                        }}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${jobTab === 'students'
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                      >
                        Student Workers
                      </button>
                      <button
                        onClick={() => {
                          setJobTab('applications');
                          setLocalShowJobs(true);
                        }}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${jobTab === 'applications'
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                      >
                        Applications
                      </button>
                    </div>
                  )}
                  {localShowShop && (
                    <div className="ml-8 flex space-x-4">
                      <button
                        onClick={() => setShopTab('products')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${shopTab === 'products'
                            ? 'bg-indigo-50 text-indigo-600'
                            : 'text-gray-600 hover:bg-gray-50'
                          }`}
                      >
                        Products
                      </button>
                      <button
                        onClick={() => setShopTab('orders')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${shopTab === 'orders'
                            ? 'bg-indigo-50 text-indigo-600'
                            : 'text-gray-600 hover:bg-gray-50'
                          }`}
                      >
                        Orders
                      </button>
                      {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                        <button
                          onClick={() => setShopTab('auctions')}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${shopTab === 'auctions'
                              ? 'bg-indigo-50 text-indigo-600'
                              : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                          Auctions
                        </button>
                      )}
                      <button
                        onClick={() => setShopTab('transactions')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${shopTab === 'transactions'
                            ? 'bg-indigo-50 text-indigo-600'
                            : 'text-gray-600 hover:bg-gray-50'
                          }`}
                      >
                        Transactions
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                      <button
                        onClick={() => setShowChat(true)}
                        className="p-2 hover:bg-indigo-50 rounded-lg transition-colors"
                      >
                        <MessageCircle className="w-6 h-6" />
                      </button>
                    )}
                    {userProfile?.mainRole !== 'CAMPUSSECURITYOFFICER' && (
                      <Notifications
                        mode="bell"
                        requests={[]}
                        checkInRequests={[]}
                        hallPasses={[]}
                        students={(userProfile?.students ?? []) as never[]}
                        items={[]}
                        onApproveRequest={() => { }}
                        onDenyRequest={() => { }}
                        onApproveCheckIn={() => { }}
                        onDenyCheckIn={() => { }}
                        onClearAllRequests={() => { }}
                        onClearAllCheckIns={() => { }}
                      />
                    )}
                  </div>
                  {userProfile?.mainRole !== 'TEACHER' && (
                    <>
                      <button
                        onClick={onOpenProfileSettings}
                        className="p-2 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="Settings"
                      >
                        <Settings className="w-5 h-5 text-gray-600" />
                      </button>
                      <button
                        onClick={() => setShowGuide(true)}
                        className="p-2 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="Help"
                      >
                        <CircleHelp className="w-5 h-5 text-gray-600" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={handleLogout}
                    className="p-2 hover:bg-indigo-50 rounded-lg transition-colors"
                  >
                    <LogOut className="w-5 h-5 text-gray-600" />
                  </button>
                  <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                    <img
                      src={userProfile?.avatar ?? undefined}
                      alt="Teacher"
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <div className="hidden sm:block">
                      <p className="text-sm font-medium text-gray-800">{userProfile?.name}</p>
                      <p className="text-xs text-gray-500">{userProfile?.org}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div> {/* closes max-w-7xl wrapper inside header */}
          </div> {/* closes the sticky header */}
          {/* Sidebar */}
          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Quick Stats */}
            {userProfile?.mainRole?.includes("TEACHER") && hasClaim?.('CanManageStudentPoints') && false && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="dashboard-card">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-50 rounded-xl">
                      <Users className="w-6 h-6 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Total Students</p>
                      <p className="text-2xl font-bold text-gray-800">24</p>
                    </div>
                  </div>
                </div>
                {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                  <div className="dashboard-card">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-green-50 rounded-xl">
                        <Award className="w-6 h-6 text-green-500" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Achievements Today</p>
                        <p className="text-2xl font-bold text-gray-800">12</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="dashboard-card">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-purple-50 rounded-xl">
                      <Star className="w-6 h-6 text-purple-500" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Average Points</p>
                      <p className="text-2xl font-bold text-gray-800">2,150</p>
                    </div>
                  </div>
                </div>
                {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                  <div className="dashboard-card">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-yellow-50 rounded-xl">
                        <TrendingUp className="w-6 h-6 text-yellow-500" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Class Level</p>
                        <p className="text-2xl font-bold text-gray-800">4.2</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Conditional rendering of main content */}
            {userProfile?.mainRole === 'CAMPUSSECURITYOFFICER' ? (
              <HallPasses key={refreshTimestamp} />
              
            ) : localShowJobs ? (
              <div className="dashboard-card p-6">
                <TeacherJobsManagement activeTab={jobTab} />
              </div>
            ) : localShowCoupons ? (
              <CouponGenerator activeTab={couponTab} />
            ) : localShowShop ? (
              <ShopManagement activeTab={shopTab} />
            ) : localShowHallPasses ? (
              <HallPasses />
            ) : localShowUserManagement ? (
              <UserManagement />
            ) : hasClaim?.('CanManageStudentPoints') ? (
              <TeacherStudent
                filteredStudents={filteredStudents}
                selectedOrganization={selectedOrganization}
                setSelectedOrganization={setSelectedOrganization}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                organizations={organizations || []}
                getStatusColor={getStatusColor}
                setSelectedStudent={setSelectedStudent}
                setDeepLinkAssignment={setDeepLinkAssignment}
                setShowOrganizationJobAssignments={setShowOrganizationJobAssignments}
                setShowOrganizationJobs={setShowOrganizationJobs}
              />
            ) : (
              <div className="dashboard-card p-6 text-sm text-gray-600">
                You do not have permission (CanManageStudentPoints) to view student points.
              </div>
            )}

            {showOrganizationJobAssignments && (
              <OrganizationJobAssignmentsPopup
                onClose={() => {
                  setShowOrganizationJobAssignments(false);
                  setDeepLinkAssignment(null);
                }}
                deepLinkAssignment={deepLinkAssignment}
              />
            )}

            {selectedStudent && (
              <StudentCard
                studentId={selectedStudent.id}
                name={selectedStudent.name}
                points={selectedStudent.points}
                image={selectedStudent.avatar}
                onClose={() => setSelectedStudent(null)}
              />
            )}
          </div>
          {import.meta.env.VITE_ACORN_EDITION === 'full' && (
            <ChatProvider>
              <ChatComponent openState={showChat} />
            </ChatProvider>
          )}
        </div>
      </div>
      {/* Sliding Help pane */}
      <aside
        className={`bg-white border-l border-gray-200 h-screen transition-all duration-300 overflow-hidden ${showGuide ? 'w-[33%]' : 'w-0'}`}
      >
        {showGuide && <GuidePanel markdown={teacherGuide} onClose={() => setShowGuide(false)} />}
      </aside>

      {/* Coupon Reissue QR Scanner */}
      {showCouponReissue && (
        <QRScanner 
          onClose={() => setShowCouponReissue(false)}
          mode="reissue"
          onRedeemSuccess={() => {
            setShowCouponReissue(false);
          }}
        />
      )}
    </div>
  );
});

export default TeacherDashboard;